import React, { useEffect, useRef, useState } from 'react';

interface ContextMenuItem {
  label: string;
  onClick?: () => void;
  danger?: boolean;
  children?: ContextMenuItem[];
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
  parentRef?: React.RefObject<HTMLDivElement>;
}

const ContextMenuItemRender: React.FC<{
  item: ContextMenuItem;
  onClose: () => void;
}> = ({ item, onClose }) => {
  const [showSubmenu, setShowSubmenu] = useState(false);
  const itemRef = useRef<HTMLButtonElement>(null);

  const handleMouseEnter = () => {
    if (item.children) {
      setShowSubmenu(true);
    }
  };

  const handleMouseLeave = () => {
    if (item.children) {
      setShowSubmenu(false);
    }
  };

  return (
    <div
      className="qe-context-menu__item-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ position: 'relative' }}
    >
      <button
        ref={itemRef}
        className={`qe-context-menu__item${item.danger ? ' qe-context-menu__item--danger' : ''}`}
        onClick={() => {
          if (item.onClick) {
            item.onClick();
            onClose();
          }
        }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <span>{item.label}</span>
        {item.children && <span style={{ fontSize: '10px', marginLeft: '8px' }}>▶</span>}
      </button>

      {/* Submenu */}
      {showSubmenu && item.children && (
        <div
          className="qe-context-menu qe-context-menu--submenu"
          style={{
            position: 'absolute',
            left: '100%',
            top: '-5px', // Align with parent item (counteract container padding + border)
            marginLeft: '-2px', // Slight overlap to prevent gap causing hover loss
            minWidth: '140px',
          }}
        >
          {item.children.map((child, index) => (
            <ContextMenuItemRender key={index} item={child} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  );
};

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is inside any context menu (including submenus)
      const target = event.target as Element;
      if (target.closest('.qe-context-menu')) {
        return;
      }
      onClose();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Add a small delay to prevent immediate closing caused by the trigger click
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 50);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Adjust position to keep menu within viewport
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      if (rect.right > viewportWidth) {
        adjustedX = x - rect.width;
      }

      if (rect.bottom > viewportHeight) {
        adjustedY = y - rect.height;
      }

      menuRef.current.style.left = `${adjustedX}px`;
      menuRef.current.style.top = `${adjustedY}px`;
    }
  }, [x, y, items]); // Recalculate when items change (submenu might affect logic if initially expanding, but main menu depends on x,y)

  return (
    <div
      ref={menuRef}
      className="qe-context-menu"
      style={{ left: x, top: y, position: 'fixed', zIndex: 9999 }}
    >
      {items.map((item, index) => (
        <ContextMenuItemRender key={index} item={item} onClose={onClose} />
      ))}
    </div>
  );
};

export default ContextMenu;
